import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from "react"

import { useApi } from "@/hooks/UseApi"
import { useAuth } from "@/providers/AuthProvider"
import AppService from "@/services/app.service"

const DEFAULT_THEME = AppService.getDefaultTheme()

interface ThemeContextValue {
  theme: string
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const api = useApi()
  const { authStatus } = useAuth()
  const [theme, setTheme] = useState(DEFAULT_THEME)

  const toggleTheme = useCallback(() => {
    setTheme((prev: string) => prev === "light" ? "dark" : "light")
  }, [])

  useEffect(() => {
    if (authStatus !== "authenticated") {
        return
    }
    
    const loadUserTheme = async () => {
      try {
        const result = await api.user.getCurrentUser()
        setTheme(result.theme || DEFAULT_THEME)
      } catch (error) {
        console.debug("Could not load user theme:", error)
      }
    }
    loadUserTheme()
  }, [authStatus, api])

  const value = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme])

  return (
    <ThemeContext.Provider value={ value }>
      <div className="App" data-theme={theme}>
        {children}
      </div>
    </ThemeContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error("useTheme must be used within <ThemeProvider>")
  }
  return context
}